import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Product } from '@/api/models';
import { useProducts, useSettings } from '@/hooks/queries';
import { useUI } from '@/context/UIContext';
import { Rack, RackSkeleton } from '@/components/rack/Rack';
import { FocusView } from '@/components/rack/FocusView';
import { FlipTile } from '@/components/rack/FlipTile';
import { CategoryChips } from '@/components/rack/CategoryChips';
import { Marquee } from '@/components/layout/Marquee';
import { Seo } from '@/components/layout/Seo';

export default function RackPage() {
  const [params, setParams] = useSearchParams();
  const mode = params.get('view') === 'grid' ? 'grid' : 'rail';
  const { data: products, isLoading, isError, refetch } = useProducts();
  const { data: settings } = useSettings();
  const { setFocusClose } = useUI();
  const navigate = useNavigate();

  const [focusIdx, setFocusIdx] = useState<number | null>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  // Lets the header show "Close" while a piece is open.
  useEffect(() => {
    setFocusClose(open ? close : null);
    return () => setFocusClose(null);
  }, [open, close]); // eslint-disable-line react-hooks/exhaustive-deps

  const all = products ?? [];

  // Categories that have pieces, in WooCommerce's order (Products → Categories).
  // Without that list from the plugin, they follow the order pieces appear in.
  const categories = useMemo(() => {
    const used = new Set(all.flatMap((p) => p.categorySlugs));
    const fromSettings = (settings?.categories ?? []).filter((c) => used.has(c.slug));
    if (fromSettings.length) return fromSettings;
    const seen = new Map<string, { slug: string; name: string }>();
    all.forEach((p) => { if (p.category && !seen.has(p.category.slug)) seen.set(p.category.slug, p.category); });
    return [...seen.values()];
  }, [all, settings?.categories]);

  const wanted = params.get('cat');
  const cat = categories.find((c) => c.slug === wanted) ?? categories[0];
  const list = useMemo(() => (cat ? all.filter((p) => p.categorySlugs.includes(cat.slug)) : all), [all, cat]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };
  const openAt = (i: number) => { setFocusIdx(i); setOpen(true); };
  const setMode = (m: 'rail' | 'grid') => { setOpen(false); setParam('view', m === 'grid' ? 'grid' : null); };
  const setCat = (slug: string) => { setOpen(false); setFocusIdx(null); setParam('cat', slug === categories[0]?.slug ? null : slug); };
  const onDetails = (p: Product, size: string | null) => { setOpen(false); navigate(`/product/${p.slug}`, { state: { size } }); };

  return (
    <div className="rack-page view">
      <Seo
        description="Small-batch clothing, cut and sewn in Porto. Turn each piece on the rail to see it front and back."
        jsonLd={list.length ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: list.map((p, i) => ({ '@type': 'ListItem', position: i + 1, name: p.name, url: `/product/${p.slug}` })),
        } : undefined}
      />
      <h1 className="sr-only">The rail</h1>
      <div className={'stage' + (open ? ' focusing' : '') + (mode === 'grid' ? ' gridmode' : '')}>
        <div className="tabs" role="tablist" aria-label="View">
          <button role="tab" aria-selected={mode === 'rail'} onClick={() => setMode('rail')}>On the rail</button>
          <button role="tab" aria-selected={mode === 'grid'} onClick={() => setMode('grid')}>Turn them around</button>
        </div>
        {categories.length > 1 && <CategoryChips categories={categories} value={cat?.slug ?? ''} onChange={setCat} />}

        <div className="rackwrap">
          {isLoading && <RackSkeleton />}
          {isError && (
            <div className="state">
              <h2>We couldn’t load the rail</h2>
              <p>Check your connection, then try again.</p>
              <button className="explore" style={{ marginTop: 14 }} onClick={() => refetch()}>Try again</button>
            </div>
          )}
          {!isLoading && !isError && all.length === 0 && (
            <div className="state"><h2>The rail is empty</h2><p>New pieces arrive every Friday.</p></div>
          )}
          {list.length > 0 && (
            <Rack key={cat?.slug ?? 'all'} products={list} onOpen={openAt} label={categories.length > 1 ? cat?.name : undefined} swingIn={!!wanted} />
          )}
        </div>

        <div className="grid">{list.map((p) => <FlipTile key={p.id} product={p} />)}</div>

        <FocusView product={focusIdx !== null ? list[focusIdx] ?? null : null} open={open} onClose={close} onDetails={onDetails} />
      </div>
      <Marquee />
    </div>
  );
}
